/**
 * Price Display Utility
 * Converts kWh prices to local currency and renders dual pricing
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

let cachedRates = null;

export async function fetchRates() {
  if (cachedRates) return cachedRates;
  try {
    const res = await axios.get(`${API}/exchange-rates/`);
    cachedRates = res.data;
    return cachedRates;
  } catch {
    return [];
  }
}

export function getRateForLocale(rates, locale) {
  if (!rates || !rates.length) return null;
  const loc = locale?.split('-')[0] || 'en';
  return rates.find(r => r.locale === loc) || rates.find(r => r.currency === 'USD') || rates[0];
}

export function convertKwhToLocal(kwhAmount, rate) {
  if (!rate || !kwhAmount) return null;
  return kwhAmount * rate.rate;
}

export function formatLocalPrice(amount, rate) {
  if (amount == null || !rate) return '';
  const formatted = amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return `${rate.symbol}${formatted}`;
}

/**
 * Hook to get exchange rate for current locale
 */
export function useExchangeRate() {
  const { i18n } = useTranslation();
  const [rates, setRates] = useState([]);
  const [currentRate, setCurrentRate] = useState(null);

  useEffect(() => {
    fetchRates().then(data => {
      setRates(data);
      setCurrentRate(getRateForLocale(data, i18n.language));
    });
  }, [i18n.language]);

  return { rates, currentRate, convertKwhToLocal, formatLocalPrice };
}

/**
 * Dual Price Display Component
 * Shows kWh price + local currency equivalent
 */
export function DualPrice({ kwhAmount, className = '', size = 'md' }) {
  const { t } = useTranslation();
  const { currentRate } = useExchangeRate();

  if (!kwhAmount) return null;

  const localAmount = currentRate ? convertKwhToLocal(kwhAmount, currentRate) : null;
  const localFormatted = localAmount != null ? formatLocalPrice(localAmount, currentRate) : null;

  const sizeClasses = {
    sm: { kwh: 'text-sm font-semibold', local: 'text-xs' },
    md: { kwh: 'text-lg font-bold', local: 'text-sm' },
    lg: { kwh: 'text-3xl font-bold', local: 'text-base' },
    xl: { kwh: 'text-5xl font-bold', local: 'text-lg' },
  }[size] || { kwh: 'text-lg font-bold', local: 'text-sm' };

  return (
    <div className={`inline-flex flex-col ${className}`} data-testid="dual-price">
      <span className={`${sizeClasses.kwh} text-amber-900`}>
        {kwhAmount.toLocaleString()} <span className="text-amber-600 font-normal">{t('common.dianUnit')}</span>
      </span>
      {localFormatted && (
        <span className={`${sizeClasses.local} text-amber-500`}>
          ≈ {localFormatted} {currentRate?.currency}
        </span>
      )}
    </div>
  );
}
